import React, { useMemo, useCallback, useState, useEffect } from "react";
import { Edit as EditIcon } from "@mui/icons-material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import Helmet from "react-helmet";
import { pageStore, XDataGrid } from "../../common";
import {
    Grid,
    IconButton,
    Tooltip,
    FormGroup,
    Typography,
    Switch,
} from "@mui/material";
import { useData, useStatePtr } from "@pure-ptr/react";

import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import { useDispatch } from "react-redux";
import { createStringDataField, createIconDataField } from "../../util/format";

import { StringEquators } from "../../store/constants/pages";
import {
    Portlet,
    PortletBody,
    PortletHeader,
} from "../../partials/content/Portlet";
import { TableLevelAction } from "../../common";
import { DeleteConfirmation } from "../../common/GenericDeleteConfirmationDialog/DeleteConfirmationDialog";
import { resetPage, set } from "../../store/actions/pages";
import { supplementalFileIdMappingLanding } from "../../store/actions/pages";
import { errorToastify } from "../../util/toast";
import GetAppIcon from "@mui/icons-material/GetApp";
import PlanIdMappingDialog from "./PlanIdMappingDialog/PlanIdMappingDialog";
import {
    deleteSelectedPayerPlanIds,
    updatePlanPayerId,
    importPlanPayers,
} from "../../store/api/supplementalFileIdMapping";
import { getPayerContracts } from "../../store/api/supplementalFileIdMapping";

import { downloadReport } from "../../store/api/supplementalFileIdMapping";
import ImportInfoDialog from "./ImportInfoDialog/ImportInfoDialog";

const { usePageStore, useGridFilters } = pageStore(
    "supplemental-file-id-landing",
    {}
);
const DEFAULT_HIDDEN_FIELDS = [];
const DEFAULT_SORT = [];

export const SupplementalFilesPage = (params) => {
    const [refresh, setRefresh] = useState(Promise.resolve());

    const [reportData, setReportData] = useState<any>({});
    const [importInfo, setImportInfo] = useState<any>({open:false,importData:null});

    const [openDeleteDialog, setOpenDeleteDialog] = useState({
        open: false,
        rowData: null,
    });

    const [planIdsToDelete, setPlanIdsToDelete] = useState([]);
    const [openCreateUpdateDialog, setOpenCreateUpdateDialog] = useState({
        open: false,
        rowData: null,
    });
    const [planPayerEditPayload, setPlanPayerEditPayload] = useState({});
    const [importPayerPlan, setImportPayerPlan] = useState(false);

    const selectedPlanIds = useStatePtr<number[]>([]);
    const openPtr = useStatePtr(false);

    const editDialogPtr: any = useStatePtr(() => ({
        payload: null,
        open: false,
    }));

    const deleteDialogPtr: any = useStatePtr(() => ({
        rowData: null,
        open: false,
    }));
    const dispatch = useDispatch();

    const payerSetupData = usePageStore((s: any) => {
        return s?.list;
    });

    const tableFilter = useGridFilters();



    const pending = usePageStore((s) => s.pending);
    const onlyShowUnmapped = usePageStore((s) => s?.onlyShowUnmapped);
    // const tableFilter: any = usePageStore((s) => s);

    function handleRefresh() {
        dispatch(set("supplemental-file-id-landing", "list", []));
        setRefresh(Promise.resolve());
    }

    useEffect(() => {
        const pageName = { name: "supplemental-file-id-landing" };
        dispatch(set(pageName.name, "waitForCriteriaFilter", false));
        return () => {
            dispatch(resetPage(pageName.name));
        };
    }, [dispatch]);

     const { data: payerContracts,isPending:isFetchingPayerContracts } = useData(async () => {
            const payerContracts = await getPayerContracts();
    
            return payerContracts;
        }, []);

    const {
        data: deleteResponse,
        isPending: isDeletingSelectedPayerIds,
        error: deleteError,
    } = useData(async () => {
        if (planIdsToDelete?.length <= 0) return;

        const deletePayerIdsResponse = await deleteSelectedPayerPlanIds(
            planIdsToDelete
        );
        setPlanIdsToDelete([]);
        deleteDialogPtr.set({ open: false, rowData: null });

        handleRefresh();
        return deletePayerIdsResponse;
    }, [planIdsToDelete]);

    function downloadTemplateCallback(blob) {
        const displayName = `Payer_Plan_Contracts`;
        let url = window.URL.createObjectURL(blob);
        let a = document.createElement("a");
        a.href = url;
        a.download = `${displayName.replace(/\s/g, "")}.${"csv"}`;
        a.click();
    }

    const { isPending: isExportingReport } = useData(async () => {
        if (Object.keys(reportData)?.length <= 0) return;
        const { payload, onlyShowUnmapped } = reportData;
        const payerContracts = await downloadReport(payload, onlyShowUnmapped);

        downloadTemplateCallback(payerContracts);
        setReportData({})
        return payerContracts;
    }, [reportData]);

    const {
        // data,
        isPending: isUpdatingPricerSetup,
        error: updateError,
    } = useData(async () => {
        if (Object.keys(planPayerEditPayload)?.length === 0) return;
        const updateResponse = await updatePlanPayerId(planPayerEditPayload);
        setPlanPayerEditPayload({});
        editDialogPtr.set({
            payload: null,
            open: false,
        });
        handleRefresh();
        return updateResponse;
    }, [planPayerEditPayload]);

    const {
        // data,
        isPending: isImportingPayerPlan,
        error: payerPlanImportError,
    } = useData(async () => {
        if (!importPayerPlan) return;
        const payerPlansImport = await importPlanPayers();
        setImportPayerPlan(false);
        
        handleRefresh();
        setImportInfo({open:true,importData:payerPlansImport?.payerPlans})
        return payerPlansImport;
    }, [importPayerPlan]);

    function handleDowloadTemplate() {
        let operandsWithoutBoth = tableFilter.filters?.operands.filter(
            (operand) =>
                !operand.filters.some((filter) => filter.value === "both")
        );
        let filterWithoutBoth = {
            ...(tableFilter.filters || {}),
            operands: operandsWithoutBoth || undefined,
        };

        const payload = {
            page: {},
            filter: filterWithoutBoth || null,
            sort: tableFilter.sort || [],
        };

        setReportData({ payload,onlyShowUnmapped});
    }

    useEffect(() => {
        if (!payerPlanImportError) return;

        errorToastify(
            payerPlanImportError?.payload?.error || "Something Went Wrong !!!"
        );
    }, [payerPlanImportError]);

    return (
        <>
            <Helmet title="Claim Payer Mapping" />
            <Portlet>
                <PortletHeader
                    title={"Payer Mapping"}
                    name="supplemental-file-id-landing"
                    showSearchFilters={true}
                    showSearchFiltersDropdown={false}
                    handleRefresh={handleRefresh}
                />
                <PortletBody>
                    <GridLevelActions
                        setImportPayerPlan={setImportPayerPlan}
                        editDialogPtr={editDialogPtr}
                        deleteDialogPtr={deleteDialogPtr}
                        handleDowloadTemplate={handleDowloadTemplate}
                        openPtr={openPtr}
                        dispatch={dispatch}
                        loading={
                            isExportingReport !== null ||
                            isImportingPayerPlan !== null
                        }
                        setOpen={() =>
                            setOpenDeleteDialog({
                                ...openDeleteDialog,
                                open: true,
                            })
                        }
                        setOpenCreateUpdateDialog={() =>
                            setOpenCreateUpdateDialog({
                                ...openCreateUpdateDialog,
                                open: true,
                            })
                        }
                        handleRefresh={handleRefresh}
                        onlyShowUnmapped={onlyShowUnmapped}
                    />

                    <PlanIdMappingDataGrid
                        loading={pending || isFetchingPayerContracts!==null}
                        data={payerSetupData}
                        selected={selectedPlanIds}
                        refresh={refresh}
                        dispatch={dispatch}
                        setRowData={(rowData) =>
                            setOpenCreateUpdateDialog({ open: true, rowData })
                        }
                        editDialogPtr={editDialogPtr}
                        deleteDialogPtr={deleteDialogPtr}
                        onlyShowUnmapped={onlyShowUnmapped}
                        setOpenDeleteDialog={setOpenDeleteDialog}
                        payerContracts={payerContracts}
                    />
                </PortletBody>

                <DeleteConfirmation
                    defaultSelected={selectedPlanIds?.value || []}
                    handleDelete={(ids) => setPlanIdsToDelete([...ids])}
                    handleClose={() =>
                        deleteDialogPtr.set({ open: false, rowData: null })
                    }
                    // isDeleting={isDeletingSelectedPayerIds !== null}
                    DELETE_TEXT={
                        "Are you sure you want to delete selected items ?"
                    }
                    isPending={isDeletingSelectedPayerIds !== null}
                    defaultDeleteState={deleteDialogPtr?.value}
                    canDelete={true}
                    NO_ITEMS_TEXT="No records selected, Please select at least 1 to delete"
                />

                <PlanIdMappingDialog
                    isLoading={isUpdatingPricerSetup !== null}
                    update={(payload) =>
                        setPlanPayerEditPayload({ ...payload })
                    }
                    open={editDialogPtr?.value?.open}
                    rowData={editDialogPtr?.value?.payload}
                    handleClose={() =>
                        editDialogPtr.set({ open: false, payload: null })
                    }
                    selected={selectedPlanIds?.value}
                    payerContracts={payerContracts}

                />
            </Portlet>
            <ImportInfoDialog
                open={importInfo?.open}
                importData={importInfo?.importData}
                handleClose={() =>
                    setImportInfo({ open: false, importData: null })
                }
            />
        </>
    );
};

const GridLevelActions = ({
    dispatch,
    loading,
    setOpen,
    setOpenCreateUpdateDialog,
    handleRefresh,
    openPtr,
    onlyShowUnmapped,
    handleDowloadTemplate,
    editDialogPtr,
    deleteDialogPtr,
    setImportPayerPlan,
}) => {
    return (
        <TableLevelAction
            handleRefresh={() => handleRefresh()}
            actions={() => (
                <>
                    <Grid item>
                        <FormGroup>
                            <Tooltip title="Toggle to view Unmapped IDS">
                                <Typography component="div">
                                    <Grid
                                        component="label"
                                        container
                                        alignItems="center"
                                        spacing={0}
                                        className="mb-0"
                                    >
                                        <Grid
                                            item
                                            sx={{
                                                fontSize: "11px",
                                                fontWeight: "500",
                                                pr: "10px",
                                            }}
                                        >
                                            Only Show Unmapped IDS
                                        </Grid>
                                        <Grid item>
                                            <Switch
                                                size="small"
                                                // disabled={pending}
                                                checked={onlyShowUnmapped}
                                                onChange={(e) => {
                                                    dispatch(
                                                        set(
                                                            "supplemental-file-id-landing",
                                                            "onlyShowUnmapped",
                                                            e.target.checked
                                                        )
                                                    );
                                                    handleRefresh();
                                                }}
                                                name="userActive"
                                            />
                                        </Grid>
                                    </Grid>
                                </Typography>
                            </Tooltip>
                        </FormGroup>
                    </Grid>
                    <Grid item>
                        <Tooltip title="Export">
                            <IconButton
                                aria-label="create"
                                size="small"
                                onClick={() => handleDowloadTemplate()}
                                disabled={loading}
                            >
                                <GetAppIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid>

                    <Grid item>
                        <Tooltip title="Import">
                            <IconButton
                                aria-label="create"
                                size="small"
                                onClick={() => setImportPayerPlan(true)}
                                disabled={loading}
                            >
                                <i className="fas fa-upload"></i>{" "}
                            </IconButton>
                        </Tooltip>
                    </Grid>

                    <Grid item>
                        <Tooltip title="Edit">
                            <IconButton
                                aria-label="Edit"
                                size="small"
                                onClick={() =>
                                    editDialogPtr.set({
                                        open: true,
                                        payload: false,
                                    })
                                }
                                disabled={loading}
                            >
                                <EditIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid>

                    <Grid item>
                        <Tooltip title="Delete">
                            <IconButton
                                aria-label="delete"
                                size="small"
                                onClick={() =>
                                    deleteDialogPtr.set({
                                        open: true,
                                        rowData: false,
                                    })
                                }
                                disabled={loading}
                            >
                                <DeleteIcon />{" "}
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </>
            )}
            loading={loading}
        />
    );
};

const PlanIdMappingDataGrid = ({
    refresh,
    data,
    loading,
    dispatch,
    selected,
    setRowData,
    onlyShowUnmapped,
    setOpenDeleteDialog,
    editDialogPtr,
    deleteDialogPtr,
    payerContracts
}) => {

    const fields = useMemo(
        () => [
            createStringDataField("payerName", "Payer Name", {
                sortable: true,
                equators: StringEquators,
            }),
            createStringDataField("payerType", "Payer Type", {
                sortable: true,
                equators: StringEquators,
            }),
            createStringDataField("planId", "Plan Id", {
                sortable: true,
                equators: StringEquators,
            }),
            createStringDataField(
                "payerContract.displayName",
                "Payer Contract",
                {
                    sortable: true,
                    equators: StringEquators,
                    secondLevel: "displayName",
                    onHover: true,
                    isDropdown: true,
                    dropdownValues:payerContracts?.map((item:any)=>item?.displayName)  || [] ,
                    width: "100px",
                }
            ),

            createIconDataField("active", "Active", {
                hideFromFilter: true,
                icon: "fas fa-check-circle",
                sortable: true,

                renderCell: (params) => {
                    return params?.value ? (
                        <i
                            style={{ fontSize: "20px" }}
                            className="fas fa-check-circle text-success ml-2 null"
                        ></i>
                    ) : (
                        <i
                            style={{ fontSize: "20px" }}
                            className="fas fa-check-circle text-secondary ml-2 null"
                        ></i>
                    );
                },
            }),
        ],
        [payerContracts]
    );

    const tableFilter = useGridFilters();

    const PlanIdMappingActions = useCallback(
        (props) => [
            <GridActionsCellItem
                icon={<EditIcon sx={{ color: "#5c79ff" }} />}
                label="Edit"
                onClick={() => {
                    editDialogPtr.set({ open: true, payload: props.row });
                }}
            />,

            <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                onClick={() =>
                    deleteDialogPtr.set({ open: true, rowData: props.row })
                }
                color="primary"
            />,
        ],
        []
    );

    return (
        <XDataGrid
            name="supplemental-file-id-landing"
            fields={fields}
            loading={loading}
            data={data || []}
            // data={[]}

            paginationFilters={tableFilter}
            onLoad={(sort, filter, pagination) =>
                dispatch(
                    supplementalFileIdMappingLanding(
                        pagination,
                        sort,
                        filter,
                        onlyShowUnmapped
                    )
                )
            }
            selectedIdsPtr={selected}
            refresh={refresh}
            defaultSort={DEFAULT_SORT}
            criteriasSaveEnabled={true}
            actions={PlanIdMappingActions}
            showSearchFilters={true}
            defaultHiddenFields={DEFAULT_HIDDEN_FIELDS}
        />
    );
};
